///
/// EarthBrowser
///
/// Created by Matt Giger on 11/27/2025
/// Copyright (c) 2025 Geodata Labs
///

import { SelectionSphereGeometry, type SelectionSphereOptions } from "@/earthbrowser/entities/SelectionSphere/SelectionSphereGeometry"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { animation } from "@stovokor/math/animation"
import { matrix } from "@stovokor/math/matrix"
import { vec3 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type StovokorContext } from "@stovokor/StovokorContext"

export class SelectionSphereNode extends NodeBase {
	public opacity: number = 0.8
	private rotationAngle = 0
	private rotationAnim: animation
	private baseTransform: matrix = matrix.identity

	constructor(context: StovokorContext, options?: SelectionSphereOptions) {
		super()
		const geometry = new SelectionSphereGeometry(context, options)
		this.renderer = new SelectionSphereRenderer(context, geometry)

		// Setup continuous rotation animation: 2 degrees per second = 180 seconds for full rotation
		this.rotationAnim = new animation()
			.from(0)
			.to(Math.PI * 2)
			.duration(180)
			.onUpdate((value) => {
				this.rotationAngle = value
				this.updateTransform()
			})

		// Start rotating immediately
		this.rotationAnim.loop()
	}

	public override set transform(value: matrix) {
		this.baseTransform = value
		this.updateTransform()
	}

	public override get transform(): matrix {
		return super.transform
	}

	private updateTransform(): void {
		const rotation = matrix.axisRotation(vec3.unitY, this.rotationAngle)
		super.transform = rotation.multiply(this.baseTransform)
	}

	public stopRotation(): void {
		this.rotationAnim.stop()
	}

	public startRotation(): void {
		this.rotationAnim.loop()
	}
}

class SelectionSphereRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public modelMatrix = new UniformDynamic<matrix>()

	constructor(context: StovokorContext, geometry: Geometry) {
		super(context, new ShaderSourceDefinition("selection_sphere"), geometry, [])
		this.transparency = true // Shader outputs alpha < 1.0
		// Disable backface culling so both sides of geometry are visible
		this.drawState.cullFace = false
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
		this.modelMatrix.value = state.modelMatrix
	}
}